/**
 * Animation element creation
 * Handles creating and positioning animation elements
 */

import { gsap } from 'gsap'
import { getSceneImagePath, applyResponsiveImage } from '../../../utils/pathResolver'
import { normalizeTransformOrigin } from './utils'
import type { SceneData } from '../types'

export interface AnimationElementContext {
  data: SceneData
  animationConfig: any // AnimationConfig type
}

/**
 * Create an animation element
 */
export function createAnimationElement(context: AnimationElementContext): HTMLImageElement {
  const animElement = document.createElement('img')
  animElement.src = getSceneImagePath(context.data.id, context.animationConfig.image)
  animElement.className = 'animation-element'
  animElement.style.position = 'absolute'
  animElement.style.width = `${context.animationConfig.dimensions.width}%`
  animElement.style.height = 'auto'
  animElement.alt = 'Animation element'
  applyResponsiveImage(animElement)

  // Handle horizontal position (left or right)
  if (context.animationConfig.position.left !== undefined) {
    if (typeof context.animationConfig.position.left === 'number') {
      animElement.style.left = `${context.animationConfig.position.left}%`
    } else {
      animElement.style.left = `${context.animationConfig.position.left.from}%`
    }
  }

  if (context.animationConfig.position.right !== undefined) {
    if (typeof context.animationConfig.position.right === 'number') {
      animElement.style.right = `${context.animationConfig.position.right}%`
    } else {
      animElement.style.right = `${context.animationConfig.position.right.from}%`
    }
  }

  // Handle vertical position (top or bottom)
  if (context.animationConfig.position.top !== undefined) {
    if (typeof context.animationConfig.position.top === 'number') {
      animElement.style.top = `${context.animationConfig.position.top}%`
    } else {
      animElement.style.top = `${context.animationConfig.position.top.from}%`
    }
  }

  if (context.animationConfig.position.bottom !== undefined) {
    if (typeof context.animationConfig.position.bottom === 'number') {
      animElement.style.bottom = `${context.animationConfig.position.bottom}%`
    } else {
      animElement.style.bottom = `${context.animationConfig.position.bottom.from}%`
    }
  }

  // Handle scale
  if (context.animationConfig.scale !== undefined) {
    const transformOrigin = normalizeTransformOrigin(context.animationConfig.transformOrigin || 'center center')
    if (typeof context.animationConfig.scale === 'number') {
      gsap.set(animElement, { scale: context.animationConfig.scale, transformOrigin })
    } else {
      gsap.set(animElement, { scale: context.animationConfig.scale.from, transformOrigin })
    }
  }
  
  // Handle rotate (initial setup - rotation will be animated in scroll handler)
  const sortedKf = context.animationConfig.keyframes?.slice().sort((a: any, b: any) => a.at - b.at)
  const hasKeyframes = sortedKf && sortedKf.length > 0
  const initialRotate = hasKeyframes && sortedKf.some((k: any) => k.rotate !== undefined)
    ? sortedKf.find((k: any) => k.rotate !== undefined)!.rotate
    : context.animationConfig.rotate?.from
  if (initialRotate !== undefined) {
    const transformOrigin = normalizeTransformOrigin(context.animationConfig.transformOrigin || 'center center')
    gsap.set(animElement, { rotation: initialRotate, transformOrigin })
  } else if (context.animationConfig.transformOrigin && !context.animationConfig.scale) {
    gsap.set(animElement, { transformOrigin: normalizeTransformOrigin(context.animationConfig.transformOrigin) })
  }

  return animElement
}

/**
 * Create animations container
 */
export function createAnimationsContainer(clipPath?: {
  top?: number
  right?: number
  bottom?: number
  left?: number
}): HTMLDivElement {
  const animationsContainer = document.createElement('div')
  animationsContainer.className = 'animations-container'
  animationsContainer.style.position = 'absolute'
  animationsContainer.style.top = '0'
  animationsContainer.style.left = '0'
  animationsContainer.style.width = '100%'
  animationsContainer.style.height = '100%'
  animationsContainer.style.pointerEvents = 'none'
  animationsContainer.style.zIndex = '2'

  if (clipPath) {
    const t = clipPath.top ?? 0
    const r = clipPath.right ?? 0
    const b = clipPath.bottom ?? 0
    const l = clipPath.left ?? 0
    animationsContainer.style.clipPath = `inset(${t}% ${r}% ${b}% ${l}%)`
  }

  return animationsContainer
}
